<template>

  <v-dialog :model-value="modelValue" max-width="600" @update:model-value="$emit('update:modelValue', $event)">

    <v-card>
      <v-card-title class="d-flex align-center justify-space-between">
        <div>
          <span class="text-h5">
            {{ dialogTitle }}
          </span>
        </div>
        <v-btn icon="mdi-close" variant="text" size="small" @click="handleClose" aria-label="Fechar" />
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-6" v-if="(authStore.isAdmin() || hasSpecialLevel) && !isViewing && isUserInvolved">
        <v-form ref="formRef" v-model="formValid" @submit.prevent="handleSave">
          <v-row>

            <v-col cols="12" v-if="!authStore.isAdmin()">
              <v-text-field v-model="formData.ordem" type="text" variant="outlined" label="Ordem" :rules="ordemRules"
                :hint="ordemHint" persistent-hint required @update:model-value="handleOrderChange"
                :readonly="!canEditLastDigit" />
            </v-col>

            <v-col cols="12" v-else>
              <v-text-field v-model="formData.ordem" type="text" variant="outlined" label="Ordem" :rules="ordemRules"
                :hint="ordemHint" persistent-hint required />
            </v-col>

            <v-col cols="12">
              <v-text-field v-model="formData.title" :label="parentId ? 'Título da Subtarefa' : 'Título da Atividade'"
                :rules="titleRules" variant="outlined" required autofocus />
            </v-col>

            <v-col cols="12">
              <v-textarea v-model="formData.description" label="Descrição" :rules="descriptionRules" variant="outlined"
                rows="3" auto-grow />
            </v-col>

            <v-col cols="12" sm="6">
              <v-select v-model="formData.status" :items="statusOptions" :loading="loadingStatus" label="Status"
                variant="outlined" :rules="[v => !!v || 'Status é obrigatório']" return-object>
                <template #selection="{ item }">
                  <v-chip :color="getStatusColor(item.raw.value)" size="small" variant="flat">
                    {{ item.title }}
                  </v-chip>
                </template>
                <template #item="{ props, item }">
                  <v-list-item v-bind="props" :title="item.title">
                    <template #prepend>
                      <v-icon :color="getStatusColor(item.raw.value)">
                        {{ getStatusIcon(item.raw.value) }}
                      </v-icon>
                    </template>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field v-model="formData.dueDate" label="Data de Vencimento" type="date" variant="outlined"
                :rules="dueDateRules" required />
            </v-col>

            <v-col cols="12">
              <v-select v-model="formData.assignees" :items="availableUsers" :loading="loadingUsers"
                label="Responsáveis" variant="outlined" multiple chips clearable item-title="nome" item-value="id"
                return-object>
                <template #chip="{ props, item }">
                  <v-chip v-bind="props" closable>
                    <v-avatar start>
                      <v-icon>mdi-account</v-icon>
                    </v-avatar>
                    {{ item.raw.nome }}
                  </v-chip>
                </template>
                <template #item="{ props, item }">
                  <v-list-item v-bind="props" :title="item.raw.nome" :subtitle="item.raw.email || item.raw.usuario">
                    <template #prepend>
                      <v-avatar>
                        <v-icon>mdi-account</v-icon>
                      </v-avatar>
                    </template>
                    <template #append>
                      <v-chip v-if="item.raw.administrador === '1'" size="x-small" color="primary" variant="flat">
                        Admin
                      </v-chip>
                      <v-chip v-if="item.raw.ativo === '0'" size="x-small" color="error" variant="flat">
                        Inativo
                      </v-chip>
                    </template>
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-text class="pa-6" v-if="isViewing">
        <v-row>
          <v-col cols="12">
            <div class="text-subtitle-2 text-medium-emphasis">Ordem</div>
            <div class="text-body-1">{{ formData.ordem || 'Não definida' }}</div>
          </v-col>

          <v-col cols="12">
            <div class="text-subtitle-2 text-medium-emphasis">Título</div>
            <div class="text-body-1">{{ formData.title }}</div>
          </v-col>

          <v-col cols="12">
            <div class="text-subtitle-2 text-medium-emphasis">Descrição</div>
            <div class="text-body-1">{{ formData.description || 'Não informada' }}</div>
          </v-col>

          <v-col cols="12" sm="6">
            <div class="text-subtitle-2 text-medium-emphasis">Status</div>
            <v-chip :color="getStatusColor(formData.status?.value)" size="small" variant="flat">
              {{ formData.status?.title || 'Não definido' }}
            </v-chip>
          </v-col>

          <v-col cols="12" sm="6">
            <div class="text-subtitle-2 text-medium-emphasis">Data de Vencimento</div>
            <div class="text-body-1">{{ formattedDueDate || 'Não definida' }}</div>
          </v-col>

          <v-col cols="12">
            <div class="text-subtitle-2 text-medium-emphasis mb-2">Responsáveis</div>
            <v-chip v-for="assignee in formData.assignees" :key="assignee.id" class="mr-2 mb-2" size="small">
              <v-avatar start>
                <v-icon>mdi-account</v-icon>
              </v-avatar>
              {{ assignee.nome }}
            </v-chip>
            <div v-if="!formData.assignees || formData.assignees.length === 0" class="text-body-1 text-medium-emphasis">
              Nenhum responsável atribuído
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions v-if="(authStore.isAdmin() || hasSpecialLevel) && !isViewing" class="pa-4">
        <v-spacer />
        <v-btn variant=" text" @click="handleClose">
          Cancelar
        </v-btn>
        <v-btn color="primary" :loading="loading" :disabled="!formValid || loadingStatus || loadingUsers"
          @click="handleSave">
          {{ isEditing ? 'Salvar' : 'Criar' }}
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>

</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, inject } from 'vue'
import api from '@/services/api'

import {
  hasSpecialLevelUtil,
  canEditOrderUtil,
  canEditActivityUtil
} from '@/utils/permissions'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  activity: {
    type: Object,
    default: null
  },
  parentId: {
    type: [String, Number],
    default: null
  },
  isViewing: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'save', 'close'])

const formRef = ref(null)
const formValid = ref(false)
const loading = ref(false)
const loadingStatus = ref(false)
const loadingUsers = ref(false)

import { useAuthStore } from '@/stores/auth'
const authStore = useAuthStore()

const statusOptions = inject('statusOptions', [])
const getNextOrder = inject('getNextOrder', () => '1')
const activitiesManager = inject('activities', null)

const hasSpecialLevel = computed(() => {
  return hasSpecialLevelUtil(authStore)
})

const canEditOrder = computed(() => {
  if (!isEditing) return false;

  return canEditOrderUtil(props.activity, authStore)
})

const availableUsers = ref([])

const formData = ref({
  ordem: '',
  title: '',
  description: '',
  status: null,
  dueDate: '',
  assignees: []
})

const isEditing = computed(() => !!props.activity?.id)
const isViewing = computed(() => !!props.isViewing && !props.parentId)

const dialogTitle = computed(() => {
  if (props.parentId) {
    if (isEditing.value) {
      return authStore.isAdmin() ? 'Editar Subtarefa' : 'Visualizar Subtarefa'
    }
    return 'Nova Subtarefa'
  }

  if (isEditing.value) {
    return authStore.isAdmin() ? 'Editar Atividade' : 'Visualizar Atividade'
  }
  return 'Nova Atividade'
})

const isUserInvolved = computed(() => {
  if (!props.activity?.id) return true;

  const userId = authStore.getUserData()?.id;

  if (authStore.isAdmin()) return true;

  if (props.activity.authorId == userId) return true;

  if (props.activity.responsaveis?.some(r => r.id == userId)) return true;

  return false;
});

const findActivityById = (activityId) => {
  if (!activityId || !activitiesManager) return null

  let activity = activitiesManager.state.allActivities?.find(
    act => act.id.toString() === activityId.toString()
  )

  if (!activity) {
    activity = activitiesManager.state.activities?.find(
      act => act.id.toString() === activityId.toString()
    )
  }

  if (!activity) {
    const findInHierarchy = (activities, targetId) => {
      for (const act of activities) {
        if (act.id.toString() === targetId.toString()) {
          return act
        }
        if (act.children && act.children.length > 0) {
          const found = findInHierarchy(act.children, targetId)
          if (found) return found
        }
      }
      return null
    }

    activity = findInHierarchy(activitiesManager.state.hierarchyActivities || [], activityId)
  }

  return activity
}

const parentActivityTitle = computed(() => {
  if (!props.parentId) return 'Atividade Principal'

  try {
    const parentActivity = findActivityById(props.parentId)

    if (parentActivity) {

      return parentActivity.titulo || parentActivity.title || 'Atividade Principal'
    }

    return 'Atividade Principal'
  } catch (error) {
    console.error('Erro ao buscar atividade pai:', error)
    return 'Atividade Principal'
  }
})

const ordemHint = computed(() => {
  if (isEditing.value) {
    if (!authStore.isAdmin() && canEditLastDigit.value) {
      return 'Você só pode alterar o último dígito da ordem'
    }
    return 'Você pode alterar a ordem para reorganizar a atividade'
  }

  if (!authStore.isAdmin()) {
    return 'A ordem será definida automaticamente pelo sistema'
  }

  if (props.parentId) {
    const parentActivity = findActivityById(props.parentId)
    const parentOrder = parentActivity?.ordem || parentActivity?.order || '1'
    return `Exemplo: se a atividade pai é "${parentOrder}", a subtarefa será "${parentOrder}.1", "${parentOrder}.2", etc.`
  }
  return 'Exemplo: 1, 2, 3... para atividades principais'
})

const canEditLastDigit = computed(() => {
  if (authStore.isAdmin()) return true

  if (!isEditing.value) return false

  const currentOrder = props.activity?.ordem || props.activity?.order
  return currentOrder && currentOrder.includes('.')
})

const fixedOrderPart = computed(() => {
  const currentOrder = isEditing.value ?
    (props.activity?.ordem || props.activity?.order) :
    formData.value.ordem

  if (!currentOrder) return ''

  const parts = currentOrder.split('.')
  if (parts.length <= 1) return ''

  return parts.slice(0, -1).join('.')
})

const handleOrderChange = (newValue) => {
  if (authStore.isAdmin() || !isEditing.value || !fixedOrderPart.value) {
    return
  }

  if (newValue && fixedOrderPart.value) {

    if (!newValue.startsWith(fixedOrderPart.value + '.')) {

      const lastPart = newValue.split('.').pop()
      const correctedValue = `${fixedOrderPart.value}.${lastPart}`
      formData.value.ordem = correctedValue
    }
  }
}

const ordemRules = [
  v => !!v || 'Ordem é obrigatória',
  v => {
    if (!v) return true;
    const pattern = /^[0-9]+(\.[0-9]+)*$/;
    return pattern.test(v) || 'Formato inválido. Use números separados por pontos (ex: 1, 1.1, 1.2.1)';
  },
  v => {
    if (!v || authStore.isAdmin()) return true;

    const currentOrder = props.activity.ordem || props.activity.order
    if (!currentOrder) return true;

    const currentParts = currentOrder.split('.')
    const newParts = v.split('.')

    if (currentParts.length <= 1) {
      return 'Você não tem permissão para alterar a ordem da atividade principal.'
    }

    for (let i = 0; i < currentParts.length - 1; i++) {
      if (newParts[i] !== currentParts[i]) {
        return 'Você só pode alterar o último dígito da ordem.'
      }
    }

    return true
  }
]

const titleRules = [
  v => !!v || 'Título é obrigatório',
]

const descriptionRules = [
  v => !!v || 'Descrição é obrigatória'
]

const dueDateRules = [
  v => !!v || 'Data de vencimento é obrigatória',
  v => {
    if (!v) return true
    const date = new Date(v)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date >= today || 'Data de vencimento não pode ser no passado'
  }
]

const fetchStatus = async () => {
  loadingStatus.value = true
  try {
    const response = await api.get('/atividades/status')

    if (response.data.status === 200 && response.data.data) {
      statusOptions.value = response.data.data.map(status => ({
        value: mapStatusToInternal(status.status),
        title: status.status,
        id: status.id
      }))
    }
  } catch (error) {
    console.error('Erro ao buscar status:', error)
    statusOptions.value = [
      { value: 'not_started', title: 'Não Iniciada', id: '1' },
      { value: 'in_progress', title: 'Em Andamento', id: '2' },
      { value: 'completed', title: 'Concluída', id: '3' }
    ]
  } finally {
    loadingStatus.value = false
  }
}

const fetchUsers = async () => {
  loadingUsers.value = true
  try {
    const response = await api.get('/usuarios')

    if (response.data.status === 200 && response.data.data) {
      availableUsers.value = response.data.data
        .filter(user => user.ativo === '1')
        .map(user => ({
          id: user.id,
          nome: user.nome,
          email: user.email,
          usuario: user.usuario,
          administrador: user.administrador,
          ativo: user.ativo,
          ultimo_acesso: user.ultimo_acesso
        }))
    }
  } catch (error) {
    console.error('Erro ao buscar usuários:', error)
    availableUsers.value = []
  } finally {
    loadingUsers.value = false
  }
}

const mapStatusToInternal = (apiStatus) => {
  const mapping = {
    'Não Iniciada': 'not_started',
    'Em Andamento': 'in_progress',
    'Concluída': 'completed'
  }
  return mapping[apiStatus] || 'not_started'
}

const getStatusColor = (status) => {
  const colors = {
    'not_started': 'grey',
    'in_progress': 'warning',
    'completed': 'success',
    'overdue': 'error'
  }
  return colors[status] || 'grey'
}

const getStatusIcon = (status) => {
  const icons = {
    'not_started': 'mdi-circle-outline',
    'in_progress': 'mdi-clock-outline',
    'completed': 'mdi-check-circle',
    'overdue': 'mdi-alert-circle'
  }
  return icons[status] || 'mdi-circle-outline'
}

const mapAssigneesFromApi = (responsaveisArray) => {
  const assignees = []

  if (!responsaveisArray || !Array.isArray(responsaveisArray) || responsaveisArray.length === 0) {
    return assignees
  }

  try {
    responsaveisArray.forEach((responsavel) => {
      const user = availableUsers.value.find(u => u.id.toString() === responsavel.id.toString())

      if (user) {
        assignees.push(user)
      } else {
        assignees.push({
          id: responsavel.id,
          nome: responsavel.nome,
          email: '',
          usuario: '',
          administrador: '0',
          ativo: '1'
        })
      }
    })
  } catch (error) {
    console.error('Erro ao mapear responsáveis:', error)
  }

  return assignees
}

const calculateOrder = async () => {
  if (isEditing.value && props.activity) {
    return props.activity.ordem || props.activity.order || ''
  }

  try {
    const response = await api.get(`/atividades/ordem${props.parentId ? `?id=${props.parentId}` : ''}`)

    if (response.data.status === 200 && response.data.data) {
      return response.data.data
    }
  } catch (error) {
    console.error('Erro ao buscar ordem:', error)
  }

}

const resetForm = async () => {
  const calculatedOrder = await calculateOrder()

  formData.value = {
    ordem: calculatedOrder,
    title: '',
    description: '',
    status: statusOptions.length > 0 ? statusOptions[0] : null,
    dueDate: '',
    assignees: []
  }

  if (!authStore.isAdmin() && !props.parentId && !formData.value.ordem.includes('.')) {

    nextTick(() => {
      if (formRef.value) {
        const ordemInput = formRef.value.$el.querySelector('[name="ordem"]')
        if (ordemInput) {
          ordemInput.readOnly = true
        }
      }
    })
  }

  if (formRef.value) {
    formRef.value.resetValidation()
  }
}

const loadActivityData = async () => {
  if (props.activity) {

    const activityStatus = statusOptions.value.find(s =>
      s.value === props.activity.status ||
      s.title === props.activity.status ||
      s.id === props.activity.statusId
    ) || statusOptions[0]

    const assignees = mapAssigneesFromApi(props.activity.responsaveis || props.activity.assignees)

    formData.value = {
      ordem: props.activity.ordem || props.activity.order || await calculateOrder(),
      title: props.activity.titulo || props.activity.title || '',
      description: props.activity.descricao || props.activity.description || '',
      status: activityStatus,
      dueDate: props.activity.data_prazo || props.activity.dueDate || '',
      assignees: assignees
    }
  } else {
    resetForm()
  }
}

const formattedDueDate = computed(() => {
  if (!formData.value.dueDate) return ''

  const [year, month, day] = formData.value.dueDate.split('-')
  return `${day}/${month}/${year}`
})

const handleSave = async () => {
  const { valid } = await formRef.value.validate()
  if (!valid) return

  loading.value = true

  try {
    const activityData = {
      ordem: formData.value.ordem,
      titulo: formData.value.title,
      descricao: formData.value.description,
      status: formData.value.status?.id || '1',
      data_prazo: formData.value.dueDate
    }

    if (formData.value.assignees && formData.value.assignees.length > 0) {
      const assigneeIds = formData.value.assignees.map(user => user.id)
      activityData.responsaveis = assigneeIds
    } else {
      activityData.responsaveis = []
    }

    emit('save', activityData)
  } catch (error) {
    console.error('Erro ao salvar atividade:', error)
  } finally {
    loading.value = false
  }
}

const handleClose = () => {
  emit('close')
  emit('update:modelValue', false)
}

watch(() => props.modelValue, async (newValue) => {
  if (newValue) {
    await Promise.all([fetchStatus(), fetchUsers()])
    await nextTick()
    loadActivityData()
  } else {
    resetForm()
  }
})

watch(() => props.activity, () => {
  if (props.modelValue) {
    loadActivityData()
  }
})

watch(() => props.parentId, async () => {
  if (props.modelValue && !isEditing.value) {

    formData.value.ordem = await calculateOrder()
  }
})

</script>

<style scoped>
/* Estilos existentes mantidos */
</style>
